'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { streamChatResponse } from './stream-chat-response';
import { trackChatbotEvent } from './chatbot-tracking';
import type {
  ChatMessage,
  ChatThread,
  ChatbotContextValue,
  NavigationToast,
} from './chatbot-types';

// Re-export types so existing imports from './chatbot-provider' keep working
export type { ChatCta, ChatMessage, ChatThread, NavigationToast } from './chatbot-types';

const ChatbotContext = createContext<ChatbotContextValue | null>(null);

export const useChatbot = () => {
  const ctx = useContext(ChatbotContext);
  if (!ctx) throw new Error('useChatbot must be used within ChatbotProvider');
  return ctx;
};

type ChatbotProviderProps = {
  tenantId: string;
  greeting?: string | null;
  children: React.ReactNode;
};

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const storageKey = (tenantId: string) => `chatbot_threads_${tenantId}`;

function loadThreads(tenantId: string): ChatThread[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (raw) return JSON.parse(raw) as ChatThread[];
  } catch {
    // Ignore parse errors
  }
  return [];
}

function saveThreads(tenantId: string, threads: ChatThread[]): void {
  if (typeof window === 'undefined') return;
  try {
    // Only persist threads that have at least one user message
    const toSave = threads.filter((t) => t.messages.some((m) => m.role === 'user'));
    localStorage.setItem(storageKey(tenantId), JSON.stringify(toSave));
  } catch {
    // Storage full or unavailable
  }
}

function makeThread(): ChatThread {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    title: 'New chat',
    startedAt: now,
    updatedAt: now,
    messages: [],
  };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const ChatbotProvider: React.FC<ChatbotProviderProps> = ({
  tenantId,
  greeting,
  children,
}) => {
  const [threads, setThreads] = useState<ChatThread[]>(() => loadThreads(tenantId));
  const [activeThreadId, setActiveThreadId] = useState<string | null>(
    () => loadThreads(tenantId)[0]?.id ?? null,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [navigationToast, setNavigationToast] = useState<NavigationToast | null>(null);
  const router = useRouter();

  const dismissNavigationToast = useCallback(() => {
    setNavigationToast(null);
  }, []);

  const previousResponseIdRef = useRef<string | undefined>(
    loadThreads(tenantId)[0]?.previousResponseId,
  );

  // Pending message ref — used to defer sendMessage until a new thread is active
  const pendingMessageRef = useRef<string | null>(null);

  // Derive messages from active thread
  const messages: ChatMessage[] =
    threads.find((t) => t.id === activeThreadId)?.messages ?? [];

  // Persist threads to localStorage whenever they change
  useEffect(() => {
    saveThreads(tenantId, threads);
  }, [threads, tenantId]);

  // Send pending message once the new thread is active
  useEffect(() => {
    if (activeThreadId && pendingMessageRef.current) {
      const msg = pendingMessageRef.current;
      pendingMessageRef.current = null;
      sendMessage(msg);
    }
  }, [activeThreadId]); // only re-run when activeThreadId changes

  // ---------------------------------------------------------------------------
  // Internal thread updaters
  // ---------------------------------------------------------------------------

  const updateActiveThread = useCallback(
    (updater: (thread: ChatThread) => ChatThread) => {
      setThreads((prev) =>
        prev.map((t) => (t.id === activeThreadId ? updater(t) : t)),
      );
    },
    [activeThreadId],
  );

  const addMessage = useCallback(
    (msg: ChatMessage) => {
      setThreads((prev) =>
        prev.map((t) => {
          if (t.id !== activeThreadId) return t;
          const updatedMessages = [...t.messages, msg];
          const title =
            t.title === 'New chat' && msg.role === 'user'
              ? msg.content.slice(0, 40)
              : t.title;
          return { ...t, messages: updatedMessages, title, updatedAt: new Date().toISOString() };
        }),
      );
    },
    [activeThreadId],
  );

  const updateLastAssistantMessage = useCallback(
    (updater: (msg: ChatMessage) => ChatMessage) => {
      setThreads((prev) =>
        prev.map((t) => {
          if (t.id !== activeThreadId) return t;
          const msgs = [...t.messages];
          let lastMsg: ChatMessage | undefined;
          let lastIdx = -1;
          for (let i = msgs.length - 1; i >= 0; i--) {
            const msg = msgs[i];
            if (msg?.role === 'assistant') {
              lastMsg = msg;
              lastIdx = i;
              break;
            }
          }
          if (!lastMsg) return t;
          msgs[lastIdx] = updater(lastMsg);
          return { ...t, messages: msgs, updatedAt: new Date().toISOString() };
        }),
      );
    },
    [activeThreadId],
  );

  // ---------------------------------------------------------------------------
  // Thread management (public)
  // ---------------------------------------------------------------------------

  const startNewThread = useCallback(
    (addGreeting = true) => {
      const thread = makeThread();

      if (addGreeting && greeting) {
        thread.messages = [
          { id: `greeting-${thread.id}`, role: 'assistant', content: greeting, ctas: [] },
        ];
      }

      setThreads((prev) => [thread, ...prev]);
      setActiveThreadId(thread.id);
      previousResponseIdRef.current = undefined;
      trackChatbotEvent('Chatbot New Thread');
      return thread.id;
    },
    [greeting],
  );

  const loadThread = useCallback(
    (id: string) => {
      setActiveThreadId(id);
      const thread = threads.find((t) => t.id === id);
      previousResponseIdRef.current = thread?.previousResponseId;
    },
    [threads],
  );

  const deleteThread = useCallback(
    (id: string) => {
      setThreads((prev) => prev.filter((t) => t.id !== id));

      if (id === activeThreadId) {
        const remaining = threads.filter((t) => t.id !== id);
        const [firstRemaining] = remaining;
        if (firstRemaining) {
          setActiveThreadId(firstRemaining.id);
          previousResponseIdRef.current = firstRemaining.previousResponseId;
        } else {
          setActiveThreadId(null);
          previousResponseIdRef.current = undefined;
        }
      }
    },
    [activeThreadId, threads],
  );

  // ---------------------------------------------------------------------------
  // sendMessage — consumes the SSE stream from stream-chat-response
  // ---------------------------------------------------------------------------

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: content.trim(),
        ctas: [],
      };

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: '',
        ctas: [],
      };

      const currentMessages = messages;
      addMessage(userMessage);
      addMessage(assistantMessage);
      setIsLoading(true);
      trackChatbotEvent('Chatbot Message Sent', { message_count: currentMessages.length + 1 });

      try {
        const allMessages = [...currentMessages, userMessage];
        const stream = streamChatResponse(`/api/v0/tenants/${tenantId}/chat`, {
          messages: allMessages.map((m) => ({ role: m.role, content: m.content })),
          previousResponseId: previousResponseIdRef.current,
        });

        for await (const event of stream) {
          switch (event.type) {
            case 'text_delta':
              updateLastAssistantMessage((msg) => ({
                ...msg,
                content: msg.content + event.delta,
              }));
              break;

            case 'tool_result': {
              const result = event.result as Record<string, string | boolean>;
              if (result.type === 'cta') {
                updateLastAssistantMessage((msg) => ({
                  ...msg,
                  ctas: [...msg.ctas, {
                    label: result.label as string,
                    url: (result.url as string) ?? null,
                    external: result.external as boolean,
                    name: result.name as string,
                  }],
                }));
              } else if (result.type === 'navigate') {
                router.push(result.url as string);
                setIsOpen(false);
                setNavigationToast({
                  label: (result.label as string) ?? (result.url as string),
                  url: result.url as string,
                });
                trackChatbotEvent('Chatbot Navigate', {
                  url: result.url as string,
                  label: (result.label as string) ?? (result.url as string),
                });
              }
              break;
            }

            case 'done':
              previousResponseIdRef.current = event.responseId;
              updateActiveThread((t) => ({ ...t, previousResponseId: event.responseId }));
              trackChatbotEvent('Chatbot Response Complete', { message_count: currentMessages.length + 2 });
              break;

            case 'error':
              updateLastAssistantMessage((msg) => ({
                ...msg,
                content: msg.content || 'Sorry, something went wrong. Please try again.',
              }));
              trackChatbotEvent('Chatbot Error');
              break;
          }
        }
      } catch {
        updateLastAssistantMessage((msg) => ({
          ...msg,
          content: msg.content || 'Sorry, something went wrong. Please try again.',
        }));
        trackChatbotEvent('Chatbot Error');
      } finally {
        setIsLoading(false);
      }
    },
    [tenantId, messages, isLoading, addMessage, updateLastAssistantMessage, updateActiveThread, router],
  );

  // ---------------------------------------------------------------------------
  // open / close
  // ---------------------------------------------------------------------------

  const open = useCallback(
    (initialMessage?: string) => {
      setIsOpen(true);
      setNavigationToast(null);
      trackChatbotEvent('Chatbot Open');

      const activeThread = threads.find((t) => t.id === activeThreadId);
      const isStale =
        activeThread != null &&
        Date.now() - new Date(activeThread.updatedAt).getTime() > 24 * 60 * 60 * 1000;

      const needsNewThread = activeThreadId === null || !activeThread || isStale;

      if (needsNewThread) {
        if (initialMessage?.trim()) {
          pendingMessageRef.current = initialMessage.trim();
        }
        startNewThread(true);
      } else {
        if (messages.length === 0 && greeting) {
          addMessage({
            id: `greeting-${activeThreadId}`,
            role: 'assistant',
            content: greeting,
            ctas: [],
          });
        }

        if (initialMessage?.trim()) {
          sendMessage(initialMessage);
        }
      }
    },
    [activeThreadId, threads, messages.length, greeting, startNewThread, addMessage, sendMessage],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    trackChatbotEvent('Chatbot Close');
  }, []);

  return (
    <ChatbotContext.Provider
      value={{
        tenantId,
        messages,
        isOpen,
        isLoading,
        open,
        close,
        sendMessage,
        threads,
        activeThreadId,
        startNewThread: () => startNewThread(true),
        loadThread,
        deleteThread,
        navigationToast,
        dismissNavigationToast,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
};
